// Tunable constants + coordinate helpers. The runtime source of truth for numbers.
// (TECHNICAL_DESIGN.md §5 coordinates; ../Wildergrove.md §Balance for the design values.)

// ---- world geometry ----
export const TILE = 32; // world px per tile
export const CHUNK = 24; // tiles per chunk edge
export const CHUNK_PX = CHUNK * TILE;
export const CHUNK_TILES = CHUNK * CHUNK;

// ---- player ----
export const PLAYER_HP = 100;
export const PLAYER_SPEED = 140; // world px / s
export const PLAYER_RADIUS = 10;

// ---- day/night (seconds) ----
export const DAY_LEN = 240; // 4 min day
export const NIGHT_LEN = 120; // 2 min night
export const CYCLE_LEN = DAY_LEN + NIGHT_LEN;

// ---- gathering ----
export const NODE_HP = { bush: 2, tree: 6, stone: 8, iron: 12, gold: 16 } as const;
export const NODE_RESPAWN_MIN = 90;
export const NODE_RESPAWN_MAX = 120;
export const HARVEST_RANGE = TILE * 1.4;

// ---- food buffs (2 slots) ----
export const FOOD = {
  berries: { maxHp: 15, spdPct: 0, durationS: 180 },
  cooked_meat: { maxHp: 40, spdPct: 8, durationS: 360 },
} as const;
export const MAX_FOOD_BUFFS = 2;
export const REGEN_PER_S = 2; // hp/s while fed

// ---- building / campfire / warmth / embers ----
export const CAMPFIRE_RADIUS = TILE * 3; // "near a campfire" for cooking/smelting + heal aura
export const CAMPFIRE_HEAL_PER_S = 1.5; // slow heal aura when standing by a lit fire
export const CAMPFIRE_LIGHT = TILE * 4; // glow radius (visual + night-darkness relief)
export const PLACE_RANGE = TILE * 2.5; // how far you can place a build
export const SAFE_ZONE_RADIUS = TILE * 4.5; // base sanctuary around home — no damage lands inside
export const SANCTUARY_STEP = TILE * 1.5; // radius gained per upgrade level
export const SANCTUARY_MAX_LEVEL = 4; // upgrade cap (base + 4 → ~10.5 tiles)
export const SANCTUARY_BASE_GOLD = 120; // level-1 gold cost; scales with level
export const SANCTUARY_BASE_STONE = 15; // level-1 stone cost; scales with level
export const WARMED_BASE_S = 60; // base Warmed duration when you rest by a fire
export const WARMED_PER_COMFORT_S = 20; // +duration per nearby comfort object
export const WARMED_COMFORT_RADIUS = TILE * 4;
export const WARMED_SPD_PCT = 10; // Warmed speed bonus
export const WARMED_XP_MULT = 1.5; // skill-XP multiplier while Warmed
export const EMBER_PER_HOUR = 6; // embers (as wood) accrued per away-hour at a campfire
export const EMBER_CAP_HOURS = 24;

// ---- rendering budgets (TECHNICAL_DESIGN.md §5) ----
// Chunks are streamed to cover the actual viewport plus this many chunks of margin, so the
// world always fills the screen regardless of monitor width (no dark edges).
export const CHUNK_MARGIN = 1; // viewport + this many chunks each side (the CSS canvas-fill
                               // rule handles edge slivers; margin 1 already over-covers)
export const MAX_CHUNK_SPRITES = 80; // safety cap for ultrawide screens

// ---- combat ----
export const ATTACK_CD = 0.4; // s between player swings
export const ATTACK_ARC_RANGE = TILE * 1.6;
export const ATTACK_ARC_DOT = 0.0; // cos threshold: a generous 180° frontal arc (cozy feel)
export const POINT_BLANK = 32; // an adjacent enemy is always hit, regardless of facing
export const KNOCKBACK = 90; // px impulse
export const PLAYER_IFRAMES = 1.5; // s after taking a hit
export const MOB_WINDUP = 0.35; // s telegraph before a mob lands a hit
export const MOB_ATTACK_CD = 1.1; // s between mob hits
export const HITSTOP = 0.05; // s freeze on a landed hit
export const BOW_CD = 0.6;
export const BOW_SPEED = 420; // px/s
export const BOW_RANGE = TILE * 6;
export const BOW_DMG = 6;
export const RESPAWN_GOLD_LOSS = 0.2;

// ---- dungeons ----
export const DUNGEON_ENTER_RANGE = TILE * 1.6; // interact near an entrance to descend
export const SATCHEL_DEATH_RATE = 0.15; // dungeon death banks only 15% (harsh — 2× the old loss); exit/quit bank 100%
export const CHEST_OPEN_RANGE = TILE * 1.4;
export const STAIRS_RANGE = TILE * 1.4;
export const BOSS_WINDUP = 0.7; // longer telegraph than a normal mob
export const PITY_N = 4; // a legendary is guaranteed within this many boss kills
export const EMPOWER_AFFIX_STEP = 2; // affix bump per empower breakpoint (every 2nd level)
export const TIER_MAX = 3; // dungeon difficulty tiers 1..3
export const TIER_LOOT_SHIFT = 3; // each tier above 1 adds this to the effective loot floor
export const TIER_MOB_MULT = 0.25; // each tier above 1 adds this to mob stat scaling
export const CHEST_GEAR_CHANCE = 0.8; // a dungeon chest also yields a gear drop
export const MOB_GEAR_CHANCE = 0.06; // dungeon mobs occasionally drop gear
// Open-world rare drops: only the tough/rare creatures, at a low rate, rolled at a shallow floor so
// they skew Common/Uncommon — the overworld rewards exploration without out-shining dungeons.
export const OVERWORLD_GEAR_CHANCE = 0.08;
export const OVERWORLD_GEAR_FLOOR = 1;
export const OVERWORLD_GEAR_MOBS = ['direwolf', 'wraith', 'boar', 'golden_slime'];
export const BLESSINGS = [
  { id: 'atk', label: '+2 Damage', atk: 2, maxHp: 0, spdPct: 0 },
  { id: 'hp', label: '+15 Max HP', atk: 0, maxHp: 15, spdPct: 0 },
  { id: 'spd', label: '+10% Speed', atk: 0, maxHp: 0, spdPct: 10 },
] as const;

// ---- mob spawning ----
export const MOB_CAP = 20;
export const SPAWN_INTERVAL = 2.5; // s between spawn attempts
export const SPAWN_MIN_DIST = TILE * 9; // spawn just off-screen
export const SPAWN_MAX_DIST = TILE * 14;
export const DESPAWN_DIST = TILE * 20;
export const CORRUPTION_RADIUS = TILE * 5; // corrupted ground around a dungeon entrance

// ---- camera zoom ----
export const ZOOM_DESKTOP = 1.0;
export const ZOOM_MOBILE = 1.25;
export const DUNGEON_ZOOM_MULT = 1.6; // dungeons zoom in — you see less, must explore
export const MOBILE_MAX_EDGE = 700; // CSS px: below → mobile zoom + touch UI

// ---- coordinate helpers (inlineable; no ad-hoc math at call sites) ----
export const tileToWorld = (t: number): number => t * TILE;
export const worldToTile = (w: number): number => Math.floor(w / TILE);
export const chunkOf = (t: number): number => Math.floor(t / CHUNK);
export const localTile = (t: number): number => ((t % CHUNK) + CHUNK) % CHUNK;

export function worldToScreen(
  wx: number,
  wy: number,
  camX: number,
  camY: number,
  zoom: number,
  viewW: number,
  viewH: number,
): { sx: number; sy: number } {
  return {
    sx: (wx - camX) * zoom + viewW / 2,
    sy: (wy - camY) * zoom + viewH / 2,
  };
}

// ---- beta / meta ----
// Hardcoded on purpose (a Vite `define` would need mirroring in vitest and risks the golden tests);
// bump this by hand each release — it changes exactly when the CHANGELOG does. (BETA_PLAYBOOK §7.)
export const BUILD = 'Beta v0.1.0 · 2026-07-12';
// Where the in-game "Feedback" button/link points. Swap for the owner's Google Form when it exists.
export const FEEDBACK_URL = 'https://github.com/DennisR93/WilderGrove/issues/new';
// GoatCounter site code — EMPTY = analytics fully inert. The owner fills this after making the free
// account. Analytics also self-disable on localhost/127.* so dev + e2e never phone home.
export const GOAT_CODE = '';

// ---- grove restoration arc (long-goal spine; fed by grove relics from the floor-10 Hollow King) ----
// Grove relics to light each totem — escalating so the arc gets harder over time (offset by the hero
// growing stronger + faster). Extend this array to lengthen the arc in a later update. Tunable:
// owner's strict escalation is [3,3,3,3,5]; this opens with a cheap totem for a fast first hook.
export const GROVE_TOTEM_COSTS = [1, 3, 3, 3, 5, 5, 7];
export const GROVE_START_SAT = 0.78;        // world colour saturation before any restoration (subtle)
export const GROVE_MAXHP_PER_STAGE = 5;     // small permanent boon per lit totem
export const GROVE_REGEN_PER_STAGE = 0.35;  // + hp/s regen per lit totem
// bonus grove-relic chance from the floor-5 Bone Warden, by dungeon tier (a rare hard-tier treat)
export const GROVE_TIER_BONUS = [0, 0, 0.02, 0.05]; // index by tier: 1→0% · 2→2% · 3→5%
